import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Screen, ScreenTitle, PrimaryButton, ErrorNote } from '../ui/Screen.jsx'
import {
  supportsPush,
  notificationPermission,
  registerServiceWorker,
  isStandalone,
  detectPlatform,
  PLATFORM,
} from '../pwa.js'
import { applicationServerKey, vapidKeyProblem, ensurePushSubscription } from '../push.js'
import { push } from '../api.js'
import { useConfigBundle, screensOf } from '../config.js'
import { NavBack } from '../ui/NavBar.jsx'

/**
 * Step 3 of 3: notification permission.
 *
 * ASKED ON A USER GESTURE, NEVER ON PAGE LOAD. A prompt fired cold gets denied,
 * and a denial cannot be re-asked — in Safari at all, and in practice
 * everywhere. That participant is then unreachable by push for the whole study,
 * permanently, with no way back and nothing telling either of us.
 *
 * WHAT THIS SCREEN MAY ASSERT (DS-6 §0, §0.1). Push has never delivered a beep,
 * not once — the dead-push-to-email fallback has never executed end to end and
 * is unit-tested with both transports mocked. So this screen may say what
 * enabling reminders MAKES POSSIBLE and may never say that reminders will
 * arrive. DS-4 §1's earned-assertion rule, applied to a capability instead of a
 * submission.
 *
 * PUSH IS NOW THE ONLY TRANSPORT, SO THIS SCREEN IS A WALL (operator, 2026-08-28).
 * Email has been removed entirely. A participant who leaves this screen without
 * a subscription the server has accepted is told nothing for five days and
 * produces no data, and nothing anywhere would report it. So the screen mirrors
 * Install.jsx: THE ONLY WAY OFF IS SUCCEEDING. Continue exists in exactly one
 * branch — permission granted AND the server accepted a subscription — and
 * every other state offers a retry and nothing else. No Continue, no Not now,
 * no Back, no self-report, and no sentence naming email as a route.
 *
 * WHAT THIS DOES NOT COVER, stated here so nobody reads the wall as a guarantee:
 * a subscription SURVIVES the participant revoking notifications at the OS
 * level (measured on Android 15 — `hasSubscription` stays true while Android
 * drops every notification). Gating onboarding fixes day 0 and does nothing for
 * day 3. `Notification.permission` is the honest signal and it is re-read live
 * on every launch by Home's gate; if she never opens the app again, nothing in
 * this client can reach her.
 *
 * CR-13 FINDING 1, fixed here: this screen used to show "Reminders are on"
 * whenever the browser reported permission === 'granted', decoupled from
 * whether a subscription was ever created — and none ever has been. It told her
 * the reminders were working at the exact moment nothing had been established.
 * Permission is not delivery, and it is not even subscription. The assertion is
 * now gated on the server having ACCEPTED a subscription for this device; with
 * none, the screen says what actually reaches her instead.
 */
export default function Notifications() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { data: bundle } = useConfigBundle()
  const copy = screensOf(bundle)
  const [permission, setPermission] = useState(() => notificationPermission())
  const [failure, setFailure] = useState(null)
  const [busy, setBusy] = useState(false)
  /*
   * null = not yet established, true = the server accepted a subscription for
   * this device, false = it did not. NOT the same question as permission, and
   * conflating the two is the finding this screen exists to close.
   */
  const [subscribed, setSubscribed] = useState(null)
  // A prompt was opened and closed without an answer. Only ever set by the tap
  // handler, so the dismissed screen can never appear on arrival.
  const [dismissed, setDismissed] = useState(false)
  // A re-check ran and permission was still denied. Distinct from "not tried".
  const [recheckFailed, setRecheckFailed] = useState(false)

  const platform = detectPlatform()
  const iosNotInstalled = platform === PLATFORM.IOS && !isStandalone()
  /*
   * Home re-reads the gate on arrival, so this returns her there and the gate
   * decides. It does NOT assert that anything succeeded — under A2 the platform
   * is the only thing that says whether permission was granted.
   */
  const done = () => navigate('/', { replace: true })

  /*
   * DS-6 §2.2 REASON 1 IS SUPERSEDED BY A2, and this is a real conflict rather
   * than a tidy-up, so it is recorded here.
   *
   * §2.2 said: if she chose Email, never show this screen — do not spend a
   * one-shot prompt on someone who has said she does not want it. A2 says
   * onboarding blocks until permission is GRANTED, on both platforms.
   *
   * Both cannot hold. Skipping the screen for an Email participant leaves the
   * gate permanently closed behind her: Home sends her straight back here, and
   * the screen she was shown as a courtesy is overridden a tap later. That is a
   * dead end built out of two correct rules.
   *
   * A2 is the later ruling and the operator's, so the skip is removed and the
   * permission step is presented to everyone. Her channel preference still
   * decides what we SEND; it no longer decides whether this gate can open.
   * Flagged to design and the operator rather than silently dropped.
   */

  /*
   * If permission is already granted, find out whether a subscription actually
   * exists rather than assuming it from the permission. ensurePushSubscription
   * NEVER prompts — it returns early unless permission is already granted — so
   * this is safe on mount and does not spend the one-shot dialog. It is
   * idempotent and is what App already runs at launch.
   */
  /*
   * ESTABLISH THE SUBSCRIPTION. One function, used by mount, by Try again and
   * by the permission watcher, so there is exactly one description of what
   * "set up" means and all three states agree.
   *
   * TERMINAL BY CONSTRUCTION (AD-4 §4.3). With email gone this screen is a
   * wall, so a subscribe that hangs is no longer a slow screen - it is a
   * participant who can never start the study and who has no button to press.
   * The service worker, the push service and our server can each hang without
   * rejecting, so the timeout is the only thing that guarantees an end. A late
   * success still wins: it overwrites the timeout's verdict.
   */
  const establish = useCallback(async () => {
    setFailure(null)
    setBusy(true)
    const timer = setTimeout(() => {
      setBusy(false)
      setSubscribed(false)
      setFailure(new Error('That is taking longer than it should. Please try again.'))
    }, 10_000)
    try {
      const ok = await ensurePushSubscription(push.subscribe)
      setSubscribed(ok === 'granted')
    } catch {
      setSubscribed(false)
    } finally {
      clearTimeout(timer)
      setBusy(false)
    }
  }, [])

  useEffect(() => {
    if (notificationPermission() !== 'granted') return
    establish()
  }, [establish])

  /*
   * RE-TEST PERMISSION LIVE. There is no event for "the participant changed a
   * browser setting in another tab", so the only way to observe it is to read
   * Notification.permission again on demand. This never calls
   * requestPermission() — that would do nothing in the denied state anyway —
   * so it cannot spend anything.
   *
   * If it has been re-granted, finish the job: subscribe, then let Home's gate
   * see a granted permission and open.
   */
  /*
   * SELF-HEAL WITHOUT A RELOAD.
   *
   * The screen used to tell her the change "may need the page to reload", which
   * is us describing our own limitation to someone who is already stuck.
   * `navigator.permissions.query({name:'notifications'})` returns a
   * PermissionStatus that fires `change` when she flips the setting, so this
   * screen can behave exactly like the install gate: she changes it in system
   * settings, comes back, and it has already moved on.
   *
   * FEATURE-DETECTED, AND THE BUTTON STAYS. Chrome and Edge support this;
   * iOS Safari is unverified, and a self-healing screen that silently does not
   * self-heal is worse than one that never promised to. So `watching` records
   * whether the live path is actually available, the copy below says only what
   * is true on THIS device, and `Check again` remains the fallback for every
   * device where it is not.
   */
  const [watching, setWatching] = useState(false)
  useEffect(() => {
    if (!navigator.permissions?.query) return undefined
    let status = null
    let live = true
    const onChange = () => {
      const now = notificationPermission()
      setPermission(now)
      if (now === 'granted' && live) establish()
    }
    navigator.permissions
      .query({ name: 'notifications' })
      .then((s) => {
        if (!live) return
        status = s
        s.addEventListener?.('change', onChange)
        setWatching(true)
      })
      .catch(() => {
        // Unsupported, or the name is not recognised. The button covers it.
      })
    return () => {
      live = false
      status?.removeEventListener?.('change', onChange)
    }
  }, [establish])

  /*
   * Re-test permission LIVE. There is no event for "she changed a system
   * setting", so reading Notification.permission again on demand is the only
   * way to observe it. This never calls requestPermission(), so it can spend
   * nothing.
   *
   * IT NO LONGER NAVIGATES. It used to call done() the moment permission read
   * granted, whatever the subscribe returned - which is the same hole the
   * Continue below had. A re-granted permission hands her to establish(), and
   * the render decides: Continue exists only once the server has accepted a
   * subscription.
   */
  async function recheck() {
    setRecheckFailed(false)
    setBusy(true)
    try {
      const now = notificationPermission()
      setPermission(now)
      if (now !== 'granted') {
        setRecheckFailed(true)
        return
      }
    } finally {
      setBusy(false)
    }
    await establish()
  }

  async function enable() {
    setFailure(null)
    setDismissed(false)
    setBusy(true)
    /*
     * AD-4 §4.3 — EVERY LOADING STATE RESOLVES WITHIN 10 SECONDS.
     *
     * AD-1 F14 found this screen stuck on "Setting up…" indefinitely, with no
     * error and no way forward but "Not now" — and because the primary had
     * gone grey while the opt-out stayed live, the screen was quietly
     * recommending the opposite of what it meant to.
     *
     * A loading state with no terminal condition is a defect, not a state. The
     * subscribe path calls a service worker, a push service and our server, any
     * of which can hang without rejecting, so the timeout is the only thing
     * that guarantees an end.
     */
    const timer = setTimeout(() => {
      setBusy(false)
      setSubscribed(false)
      setFailure(new Error('That is taking longer than it should. Please try again.'))
    }, 10_000)
    try {
      // 1. Permission. This call must stay inside the gesture handler.
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result === 'default') {
        // Closed without an answer. We do NOT know whether she closed it or the
        // phone did, so nothing we show may attribute the act to her.
        setDismissed(true)
        return
      }
      if (result !== 'granted') return

      // 2. Service worker, then the push subscription bound to our VAPID key.
      const registration = await registerServiceWorker()
      if (!registration) throw new Error('Reminders are not supported by this browser.')
      await navigator.serviceWorker.ready

      const problem = vapidKeyProblem()
      if (problem) throw new Error(problem)

      const existing = await registration.pushManager.getSubscription()
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(),
        }))

      // 3. THE ONLY THING THAT EARNS THE ASSERTION: the server accepted it.
      await push.subscribe(subscription.toJSON())
      setSubscribed(true)
      done()
    } catch (err) {
      // Permission may have been granted and the subscription still failed.
      // That is precisely the state the old screen called "Reminders are on".
      setSubscribed(false)
      setFailure(err)
    } finally {
      setBusy(false)
    }
  }

  /*
   * NO PUSH ON THIS BROWSER. This used to be a Continue and a promise of email.
   * With push the only transport, a browser that cannot receive one cannot run
   * the study, and letting her past would enrol someone we can never reach.
   *
   * No action is passed, so Screen renders NO BAND rather than a disabled one -
   * the same shape Install uses on iOS, where the step is not completed by
   * pressing anything on this screen.
   */
  if (!supportsPush()) {
    return (
      <Screen disposition="status" nav={{ right: 'Step 3 of 3' }}>
        <ScreenTitle>This browser can&rsquo;t receive check-ins</ScreenTitle>
        <p className="mb-4 text-base leading-relaxed text-[var(--ink-700)]">
          Editrics tells you a check-in is ready by sending a notification, and this browser
          cannot receive one.
        </p>
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          Open Editrics on an up-to-date iPhone or Android phone, add it to your home screen,
          and carry on from there.
        </p>
      </Screen>
    )
  }

  /*
   * GRANTED IS NOT ENOUGH. Only a subscription the server accepted earns a
   * screen that says reminders are set up — and even then it says SET UP, never
   * that they will arrive.
   */
  if (permission === 'granted' && subscribed === true) {
    return (
      <Screen disposition="status" nav={{ left: <NavBack />, right: 'Step 3 of 3' }} action={<PrimaryButton onClick={done}>Continue</PrimaryButton>}>
        <ScreenTitle>Reminders are set up on this phone</ScreenTitle>
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          You&rsquo;ll get six short check-ins a day for five days. Each one is a dozen questions at
          most.
        </p>
      </Screen>
    )
  }

  /*
   * GRANTED, BUT NOTHING WAS ESTABLISHED - AND THIS IS NOW A WALL.
   *
   * It used to offer Continue and say "We will email you each check-in". Both
   * were wrong the moment email stopped existing: the sentence became a lie,
   * and the button walked her into a five-day study that could not reach her.
   * She would answer 178 day-0 items and then hear nothing for five days.
   *
   * So it mirrors Install.jsx: the only way off is succeeding. No Continue, no
   * Back, no self-report. The blame is ours out loud, because it is ours - she
   * did the one thing this screen asked and the failure is on our side of the
   * wire.
   */
  if (permission === 'granted' && subscribed === false) {
    return (
      <Screen
        disposition="status"
        nav={{ right: 'Step 3 of 3' }}
        action={
          <PrimaryButton onClick={establish} disabled={busy}>
            {busy ? 'Trying again…' : 'Try again'}
          </PrimaryButton>
        }
      >
        <ScreenTitle>Reminders aren&rsquo;t set up yet</ScreenTitle>
        <p className="mb-4 text-base leading-relaxed text-[var(--ink-700)]">
          You allowed notifications, but we couldn&rsquo;t finish setting them up on this phone.
          That is our end, not anything you have done.
        </p>
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          A check-in reminder is the only way we reach you, so this has to work before Editrics
          can start. Try again now, or close Editrics and open it again from your home screen in
          a few minutes.
        </p>
        <ErrorNote error={failure} />
      </Screen>
    )
  }

  /*
   * DENIED — THE ONE STATE THAT MUST NOT BE A WALL.
   *
   * Under A2 onboarding blocks until permission is granted. That is correct for
   * someone who has not answered: the prompt is still there and the gate is
   * what makes her use it.
   *
   * It is NOT correct here. The prompt fires once and the browser never
   * re-asks, so from inside this app there is nothing she can press to change
   * this. A block with no escape would strand her permanently at the last gate
   * before day 0 — unable to proceed, unable to fix it, and invisible to us
   * because nothing reports a participant who simply stopped.
   *
   * So this screen carries three things and it needs all three:
   *   1. HOW to reverse it, since the fix is outside the app and she will not
   *      guess where it lives.
   *   2. A RE-CHECK that re-tests permission LIVE. Nothing else on this path
   *      can observe her changing a browser setting in another tab — there is
   *      no event for it — so without a re-check she could fix it and still be
   *      told she had not.
   *   3. A WAY FORWARD that works even if 1 and 2 fail her. Some browsers bury
   *      the setting, some managed devices remove it. She gets to the study
   *      either way; the alternative is losing the participant entirely, and
   *      her day-0 data with her.
   */
  /*
   * GRANTED, BUT THE SUBSCRIPTION HAS NOT RESOLVED YET.
   *
   * Found by the walk. The three branches above cover granted+true and
   * granted+false; `subscribed` starts as null while ensurePushSubscription is
   * in flight, and with none of them matching the screen fell through to the
   * PROMPT — asking her to turn on reminders she had already allowed. On a slow
   * device that is a flash of the wrong screen; if the subscribe never resolves
   * it is permanent, and pressing the prompt spends nothing because the browser
   * will not ask twice.
   *
   * So permission === 'granted' never reaches the prompt again. This states
   * neither that reminders are set up nor that they failed, because neither is
   * known yet, and it always offers the way forward.
   */
  if (permission === 'granted') {
    return (
      <Screen
        disposition="status"
        nav={{ right: 'Step 3 of 3' }}
        /*
         * NO ACTION AT ALL, and that is deliberate. A `disabled` primary here
         * measures 1.27:1 against AD-3's 3.0 boundary floor, and this branch is
         * held for as long as the subscribe is in flight - so it would be a
         * durable low-contrast control on the one screen that is now a wall,
         * not the momentary pressed state the disabled token was drawn for.
         *
         * Screen renders NO BAND when no action is passed, the same shape
         * Install uses on iOS. It is terminal within 10s by construction:
         * establish()'s timeout resolves this branch to Continue or Try again.
         */
      >
        <ScreenTitle>Reminders are allowed</ScreenTitle>
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          You&rsquo;ve allowed notifications on this phone. We&rsquo;re finishing the setup now.
        </p>
      </Screen>
    )
  }

  if (permission === 'denied') {
    return (
      <Screen
        nav={{ right: 'Step 3 of 3' }}
        action={
          <PrimaryButton onClick={recheck} disabled={busy}>
            {busy ? 'Checking…' : 'Check again'}
          </PrimaryButton>
        }
      >
        <ScreenTitle>Reminders are switched off</ScreenTitle>

        {/*
          NAME THE RIGHT PLACE, PER PLATFORM.
          
          This said "your browser settings for this site". Under A2 she is
          always in an INSTALLED standalone app, and in that configuration the
          generic wording is wrong on both platforms:
          
           - Android installs a WebAPK, so its notifications are Android's own
             app settings. Chrome's site settings are not where this lives, and
             sending her there means she looks, finds nothing, and concludes the
             app is broken.
           - iOS puts it under Settings > Notifications > the app.
          
          There is deliberately NO button here. Navigation to chrome:// from web
          content is blocked and no browser exposes a settings deep link, so a
          control that looked like one would be a dead tap on the screen that
          can least afford one. Words that name the real route are the most this
          screen can honestly offer.
        */}
        <p className="text-base leading-relaxed text-[var(--ink-900)]">
          Notifications for Editrics are switched off, and the browser won&rsquo;t ask again. You
          can turn them back on yourself:
        </p>

        <ol className="mt-[var(--s-4)] flex flex-col gap-[var(--s-3)]">
          {settingsRoute(platform).map((line, i) => (
            <li key={i} className="flex gap-[var(--s-3)]">
              <span className="t-meta flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--r-pill)] bg-[var(--surface-sunken)] tabular-nums text-[var(--ink-700)]">
                {i + 1}
              </span>
              <span className="text-base leading-snug text-[var(--ink-900)]">{line}</span>
            </li>
          ))}
        </ol>

        <p className="mt-[var(--s-4)] text-base leading-relaxed text-[var(--ink-700)]">
          {watching
            ? 'Come back when you have, and this screen will move on by itself.'
            : 'Then come back and use Check again.'}
        </p>

        <ErrorNote
          error={recheckFailed ? 'Still switched off. Check the steps above and try again.' : null}
        />
      </Screen>
    )
  }

  return (
    <Screen
      nav={{ left: <NavBack />, right: 'Step 3 of 3' }}
      action={
        <PrimaryButton onClick={enable} disabled={busy}>
          {busy ? 'Setting up…' : 'Turn on reminders'}
        </PrimaryButton>
      }
    >
      <ScreenTitle>Let us remind you</ScreenTitle>

      <p className="mb-4 text-base leading-relaxed text-[var(--ink-700)]">
        You&rsquo;ll get six short check-ins a day for five days. Each one is a dozen questions at
        most.
      </p>
      <p className="mb-6 text-base leading-relaxed text-[var(--ink-700)]">
        Reminders say only that it&rsquo;s time to check in. Nothing about Editrics appears on your
        lock screen.
      </p>

      {/*
        §4.2 — WHAT MAKES THIS NOT A COOKIE BANNER.

        A cookie banner asks for the asker's benefit, explains nothing and
        offers a token decline. The distinguishing move design names first is
        stating the consequence TO HER, once, before the dialog opens.

        The consequence here is unusually sharp and was nowhere on the screen:
        the browser asks ONCE. Saying so is not pressure; it is the single fact
        that makes an informed answer possible, and withholding it is what would
        make this a banner.

        §0.1's second change applies to this paragraph specifically: the screen
        must NOT be weighted up to compensate for push not working. The reflex
        when a channel fails is to press harder on the step meant to fix it,
        which would be persuading her toward a benefit never once delivered.
        Same weight, better reason.
      */}
      <p className="mb-6 rounded-[var(--r-surface)] bg-[var(--surface-sunken)] px-4 py-3 text-base leading-relaxed text-[var(--ink-900)]">
        Your phone will ask you once. If you say no, it won&rsquo;t ask again - so if you want
        reminders, this is the moment.
      </p>

      {dismissed ? (
        /*
          Appendix C. Shown only after a prompt was opened and closed without an
          answer, never on arrival — which is why `dismissed` is set solely by
          the tap handler. We do not know whether she closed it or the phone
          did, so config's sentence attributes the act to nobody.
        */
        <p
          role="status"
          className="mb-6 rounded-[var(--r-surface)] border-l-2 border-[var(--critical)] bg-[var(--surface-sunken)] px-4 py-3 text-base leading-relaxed text-[var(--ink-900)]"
        >
          {copy?.permission_dismissed?.body ??
            'The reminder prompt closed without a choice. You can open it again from here. On iPhone, the browser may not offer it a second time.'}
        </p>
      ) : null}

      {iosNotInstalled ? (
        <p className="mb-6 rounded-[var(--r-surface)] bg-[var(--surface-sunken)] px-4 py-3 text-sm text-[var(--ink-900)]">
          On iPhone, reminders can only work once Editrics is on your home screen.{' '}
          <button
            type="button"
            onClick={() => navigate('/install')}
            className="font-semibold underline"
          >
            Go back and add it
          </button>
          .
        </p>
      ) : null}

      <ErrorNote error={failure} />

      {/*
        "NOT NOW" IS GONE, AND SO IS THE SENTENCE THAT PRICED IT.

        DS-6 §4.2 asked for a decline as large and legible as the accept, and it
        was right for as long as email existed to catch her. It does not. A
        decline now means no check-in ever arrives, so the button offered a
        choice the study cannot honour — and Home sent her straight back here a
        tap later anyway, which made it a dead control on the screen that can
        least afford one.

        The one-shot warning above STAYS. It is the fact that makes her answer
        informed, it is what stops this being a cookie banner, and it is not
        persuasion.
      */}
    </Screen>
  )
}

/*
 * `EmailRoute` IS DELETED, not left unused and not reworded.
 *
 * It rendered `screens.install.email_route` — "We will email you each check-in"
 * — at seven sites on this screen. Email has been removed as a transport, so
 * every one of them was a promise the system can no longer keep, printed on the
 * exact screen a participant reads when push has just failed her.
 *
 * The config key still exists and is config's to retire. This file simply stops
 * rendering it, and nothing here may name email as a route again.
 */

/**
 * Where notifications for this app actually live, per platform.
 *
 * Under A2 the participant is always in an INSTALLED standalone app, which is
 * what makes the generic "browser settings for this site" wording wrong rather
 * than merely vague:
 *
 *  - ANDROID installs a WebAPK. To the system it is an app, so its notification
 *    switch is in Android's app settings, not in Chrome's site settings. Sending
 *    her to Chrome means she looks in the wrong place, finds nothing, and
 *    reasonably concludes the app is broken.
 *  - iOS puts every app's notifications under Settings > Notifications.
 *
 * The desktop branch exists only because the study can be opened on one; it is
 * not a supported path and the wording does not pretend otherwise.
 */
export function settingsRoute(platform) {
  if (platform === PLATFORM.IOS) {
    return [
      'Open the Settings app on your phone.',
      'Tap Notifications.',
      'Find Editrics in the list and turn Allow Notifications on.',
    ]
  }
  if (platform === PLATFORM.ANDROID) {
    return [
      'Press and hold the Editrics icon on your home screen.',
      'Tap App info, then Notifications.',
      'Turn notifications on.',
    ]
  }
  return [
    'Open your browser settings for this site.',
    'Find Notifications and set it to Allow.',
  ]
}
